import React, { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useOptions, useEntitySearch } from '../../../hooks/useCrmData';
import { getAccessToken } from '../../../authUtils';
import {
  Modal, FormField, TextInput, Select, Btn, Tabs, TypeBadge,
  ErrorBlock, Spinner, EmptyBlock, COLORS,
} from '../../ui/crm-ui';

interface Props {
  baseApiUrl: string;
  open: boolean;
  /** The job to link the (new or existing) entity to. */
  jobId: number;
  jobName?: string;
  onCreated: (entityId: number) => void;
  onClose: () => void;
}

type Mode = 'create' | 'link';

function apiError(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string } | undefined;
    return data?.error || err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const CreateEntityModal: React.FC<Props> = ({ baseApiUrl, open, jobId, jobName, onCreated, onClose }) => {
  const [mode, setMode] = useState<Mode>('create');

  // Create-new fields
  const [name, setName] = useState('');
  const [entityTypeId, setEntityTypeId] = useState<string>('');
  const [tfn, setTfn] = useState('');
  const [abn, setAbn] = useState('');
  const [xpmCode, setXpmCode] = useState('');
  const [incDate, setIncDate] = useState('');

  // Link-existing fields
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMode('create');
    setName(''); setEntityTypeId(''); setTfn(''); setAbn(''); setXpmCode(''); setIncDate('');
    setSearchInput(''); setDebouncedSearch(''); setSelectedEntityId(null);
    setSubmitting(false); setError(null);
  }, [open]);

  // 250ms debounce for entity search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const entityTypes = useOptions(baseApiUrl, open ? 'entity_types' : null);
  const search = useEntitySearch(baseApiUrl, mode === 'link' ? debouncedSearch : '');

  async function linkEntity(entityId: number, token: string) {
    await axios.post(`${baseApiUrl}/api/job-entities`, {
      job_id: jobId,
      entity_id: entityId,
      date_from: todayIso(),
    }, { headers: { Authorization: `Bearer ${token}` } });
  }

  async function handleSubmit() {
    setError(null);
    if (mode === 'create' && !name.trim()) { setError('Name is required.'); return; }
    if (mode === 'link' && !selectedEntityId) { setError('Pick an entity from the list.'); return; }
    const token = await getAccessToken();
    if (!token) { setError('Not authenticated. Please sign in and try again.'); return; }
    setSubmitting(true);
    try {
      if (mode === 'create') {
        const payload: Record<string, unknown> = { name: name.trim() };
        if (entityTypeId) payload.entity_type_id = Number(entityTypeId);
        if (tfn.trim()) payload.tax_file_number = tfn.trim();
        if (abn.trim()) payload.australian_business_number = abn.trim();
        if (xpmCode.trim()) payload.xpm_code = xpmCode.trim();
        if (incDate) payload.incorporation_date = incDate;

        const res = await axios.post<{ id: number }>(`${baseApiUrl}/api/entities`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const newId = res.data.id;
        await linkEntity(newId, token);
        onCreated(newId);
      } else {
        await linkEntity(selectedEntityId!, token);
        onCreated(selectedEntityId!);
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting ||
    (mode === 'create' && !name.trim()) ||
    (mode === 'link' && !selectedEntityId);

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title={jobName ? `Add Entity — ${jobName}` : 'Add Entity'}
      width={520}
      footer={
        <>
          <Btn onClick={onClose} disabled={submitting}>Cancel</Btn>
          <Btn primary onClick={handleSubmit} disabled={submitDisabled}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Create & link' : 'Link to job'}
          </Btn>
        </>
      }
    >
      <Tabs<Mode>
        value={mode}
        onChange={(v) => { setMode(v); setError(null); }}
        options={[
          { label: 'Create new', value: 'create' },
          { label: 'Link existing', value: 'link' },
        ]}
      />

      {mode === 'create' && (
        <>
          <FormField label="Name" required>
            <TextInput value={name} onChange={setName} placeholder="e.g. Smith Family Trust" autoFocus />
          </FormField>

          <FormField label="Entity Type">
            <Select
              value={entityTypeId}
              onChange={setEntityTypeId}
              options={(entityTypes.data || []).map((t) => ({ label: t.name, value: t.id }))}
              placeholder="(none)"
              disabled={entityTypes.loading}
            />
          </FormField>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="TFN">
              <TextInput value={tfn} onChange={setTfn} placeholder="Optional" />
            </FormField>
            <FormField label="ABN">
              <TextInput value={abn} onChange={setAbn} placeholder="Optional" />
            </FormField>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="XPM Code">
              <TextInput value={xpmCode} onChange={setXpmCode} placeholder="Optional" />
            </FormField>
            <FormField label="Incorporation date">
              <TextInput value={incDate} onChange={setIncDate} type="date" />
            </FormField>
          </div>
        </>
      )}

      {mode === 'link' && (
        <>
          <FormField
            label="Search entities"
            hint="Find an existing entity (e.g. an SMSF that already exists in another year's job) and link it to this job."
          >
            <TextInput
              value={searchInput}
              onChange={(v) => { setSearchInput(v); setSelectedEntityId(null); }}
              placeholder="Type at least 2 characters…"
              autoFocus
            />
          </FormField>

          <div style={{
            border: `1px solid ${COLORS.border}`, borderRadius: 10,
            maxHeight: 240, overflowY: 'auto', background: COLORS.white,
          }}>
            {!debouncedSearch && (
              <EmptyBlock>Start typing to search.</EmptyBlock>
            )}
            {debouncedSearch && search.loading && <Spinner label="Searching…" />}
            {debouncedSearch && !search.loading && search.error && <ErrorBlock message={search.error} />}
            {debouncedSearch && !search.loading && !search.error && search.data && search.data.length === 0 && (
              <EmptyBlock>No entities match “{debouncedSearch}”.</EmptyBlock>
            )}
            {search.data?.map((e) => {
              const active = selectedEntityId === e.id;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelectedEntityId(e.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, width: '100%', padding: '10px 14px',
                    border: 'none', borderBottom: `1px solid ${COLORS.border}`,
                    background: active ? COLORS.blueSoft : COLORS.white,
                    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: active ? COLORS.blue : COLORS.dark,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{e.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 2 }}>
                      {e.australian_business_number ? `ABN ${e.australian_business_number}` : 'No ABN'}
                    </div>
                  </div>
                  {e.entity_type_name && <TypeBadge type={e.entity_type_name} />}
                </button>
              );
            })}
          </div>
        </>
      )}

      {error && <div style={{ marginTop: 12 }}><ErrorBlock message={error} /></div>}
    </Modal>
  );
};

export default CreateEntityModal;
